import * as React from 'react';
import { Controller, useWatch, type Control, type FieldValues, type UseFormSetValue } from 'react-hook-form';
import { components, type GroupBase, type MultiValue, type SingleValue } from 'react-select';
import RSAsyncSelect from 'react-select/async';
import axios from 'axios';
import { useQuery, useQueryClient } from 'react-query';

import { useTheme } from '../theme/ThemeProvider';
import { getSemanticAttributes } from '../internals/semanticProps';
import { CheckboxSelect } from '../composites/CheckboxSelect';
import { createWealthlaneReactSelectStyles } from '../composites/Select/createWealthlaneReactSelectStyles';
import type { SelectOption } from '../composites/Select';
import { FormField } from '../patterns/FormField';

import { rhfInlineError } from './rhfInlineError';
import type { Reaction, Effect } from './reactions';
import { useReactions } from './useReactions';
import { useEffects } from './useEffects';
import type { RhfFormFieldShellProps, RhfLayoutProps } from './rhfLayout';

// ---------------------------------------------------------------------------
// Data source types (discriminated union)
// ---------------------------------------------------------------------------

export type AsyncOptionLoader = (inputValue: string) => Promise<SelectOption[]>;

export type UrlDataSource = {
  type: 'url';
  url: string;
  labelField: string;
  valueField: string;
  extraFields?: Record<string, string>;
  params?: Record<string, string | number | boolean>;
  /**
   * When set, the user's search input is sent as this query param for
   * server-side filtering. When omitted, all options are fetched once
   * and react-select filters client-side.
   */
  searchParam?: string;
  /**
   * Pins an option to the top of the list based on a field match.
   * E.g. `{ field: 'isOtherCountry', value: false }` pins "United States" to top.
   */
  pinFirst?: { field: string; value: unknown };
  /**
   * When true, auto-selects the pinFirst option as the default value
   * if the field is empty when options first load.
   */
  autoDefault?: boolean;
  /**
   * Field path this data source depends on. When the dependency changes,
   * options are refetched with the new value and current selection is cleared.
   */
  dependsOn?: string;
  /**
   * Maps dependency field paths to query param names.
   * E.g. `{ countryId: 'participant.countryId' }` sends countryId query param.
   */
  paramMap?: Record<string, string>;
};

export type FnDataSource<TItem = any> = {
  type: 'fn';
  /**
   * Stable string that uniquely identifies this data source for React Query caching.
   * REQUIRED — prevents cache key collisions when multiple fields share the same
   * `fn` closure shape (e.g. all FormFieldAdapter action loaders have identical
   * `fn.toString()` bodies). Use the actionId, lookup name, or field path.
   *
   * @example cacheKey: 'FETCH_COUNTRIES'
   * @example cacheKey: 'FETCH_STATES'  // params suffix added automatically when dataSourceParams present
   */
  cacheKey: string;
  /**
   * Optional static params merged with runtime `dataSourceParams` on every call.
   * Included in the cache key via `fnIdentity` so different param sets get
   * distinct React Query entries (cascading selects).
   */
  params?: Record<string, string | number | boolean>;
  /**
   * Called with `(search, effectiveParams)` where `effectiveParams` merges
   * static `params` with runtime `dataSourceParams` watched values.
   */
  fn: (search: string, params?: Record<string, string | number | boolean>) => Promise<TItem[] | { items?: TItem[] | null }>;
  mapper: (item: TItem) => SelectOption & Record<string, unknown>;
  /**
   * `'client'` (default) — fetches all options once, filters locally on keystroke.
   * `'server'` — calls `fn(search)` on each keystroke for server-side filtering.
   */
  searchMode?: 'client' | 'server';
};

export type CustomDataSource = {
  type: 'custom';
  loadOptions: AsyncOptionLoader;
};

export type DataSource = UrlDataSource | FnDataSource | CustomDataSource;

/** Removes options whose `value` matches any entry in `excludeOptionValues` (string compare). */
export function filterExcludedSelectOptions(
  options: SelectOption[],
  excludeOptionValues?: string[],
): SelectOption[] {
  if (!excludeOptionValues?.length) return options;
  const excluded = new Set(excludeOptionValues.map(String));
  return options.filter((o) => !excluded.has(String(o.value)));
}

type SelectOptionWithOrder = SelectOption & { displayOrder?: number; disabled?: boolean; isDisabled?: boolean };

/** Job Scheduler automation configuration parity (legacy useQuery `select` — no refetch on each click). */
export function applyDisplayOrderDisableGate(
  options: SelectOption[],
  selectedRaw: unknown,
  enabled: boolean,
): SelectOption[] {
  if (!enabled) return options;
  const selectedIds = (Array.isArray(selectedRaw) ? selectedRaw : []).map(String);
  const order1 = options.find((o) => Number((o as SelectOptionWithOrder).displayOrder) === 1);
  const isOrder1Selected = order1 ? selectedIds.includes(String(order1.value)) : false;

  return options.map((o) => {
    const opt = o as SelectOptionWithOrder;
    const id = String(opt.value);
    const disabled =
      selectedIds.includes(id) || (Number(opt.displayOrder) !== 1 && !isOrder1Selected);
    return { ...opt, isDisabled: disabled };
  });
}

/** Legacy onConfigurationChange: clear when displayOrder 1 is not in the new selection. */
export function normalizeDisplayOrderMultiSelection(values: string[], options: SelectOption[]): string[] {
  const order1 = options.find((o) => Number((o as SelectOptionWithOrder).displayOrder) === 1);
  if (!order1) return values;
  const hasOrder1 = values.some((v) => String(v) === String(order1.value));
  return hasOrder1 ? values : [];
}

// ---------------------------------------------------------------------------
// React Query cache config — shared across all async-select instances
// ---------------------------------------------------------------------------

const ASYNC_SELECT_CACHE_TIME = 0; // no cache — GC immediately on unmount
const ASYNC_SELECT_STALE_TIME = 0; // always stale — dedup only (no served-from-cache)

/**
 * Stable cache identity for `fn`-mode data sources.
 *
 * Uses `ds.cacheKey` — a required, explicit string on `FnDataSource`.
 * This prevents React Query cache key collisions that occur when multiple
 * fields share an identical `fn` closure body (e.g. all FormFieldAdapter
 * action loaders are generated from the same template; `fn.toString()`
 * produces the same string for every field, causing all dropdowns to serve
 * the first field's cached data).
 *
 * `fn.toString()` is intentionally NOT used as a fallback — it is unsafe
 * for factory-generated closures and was the root cause of the cache
 * collision bug.
 */
function fnIdentity(ds: FnDataSource): string {
  if (process.env.NODE_ENV !== 'production' && !ds.cacheKey) {
    // eslint-disable-next-line no-console
    console.warn(
      '[RhfAsyncSelect] FnDataSource is missing required `cacheKey`. ' +
        'Without a stable cacheKey, React Query may deduplicate unrelated dropdowns ' +
        'under the same cache entry, causing all of them to show identical options. ' +
        'Set cacheKey to a unique string (e.g. the actionId or lookup name).',
    );
  }
  // Include resolved params in the cache key so cascading selects (e.g.
  // FETCH_STATES filtered by countryId) get a distinct entry per parent value.
  const paramsSuffix = ds.params && Object.keys(ds.params).length > 0
    ? `:${JSON.stringify(ds.params, Object.keys(ds.params).sort())}`
    : '';
  return `${ds.cacheKey}${paramsSuffix}`;
}

// ---------------------------------------------------------------------------
// Envelope unwrapper — handles Array<T> and { items?: T[] | null }
// ---------------------------------------------------------------------------

function unwrapEnvelope<T = Record<string, unknown>>(data: unknown): T[] {
  if (Array.isArray(data)) return data as T[];
  if (data != null && typeof data === 'object') {
    if ('items' in data) {
      const items = (data as { items?: unknown }).items;
      if (Array.isArray(items)) return items as T[];
    }
    return [data as T];
  }
  return [];
}

// ---------------------------------------------------------------------------
// URL mode mapper
// ---------------------------------------------------------------------------

function mapUrlItems(
  items: Record<string, unknown>[],
  ds: UrlDataSource,
): SelectOption[] {
  const mapped = items.map((item) => {
    const option: SelectOption & Record<string, unknown> = {
      label: String(item[ds.labelField] ?? ''),
      value: String(item[ds.valueField] ?? ''),
    };
    if (ds.extraFields) {
      for (const [optionKey, dtoKey] of Object.entries(ds.extraFields)) {
        option[optionKey] = item[dtoKey];
      }
    }
    return option;
  });

  if (ds.pinFirst) {
    const { field, value } = ds.pinFirst;
    const pinIdx = mapped.findIndex((o) => (o as Record<string, unknown>)[field] === value);
    if (pinIdx > 0) {
      const [pinned] = mapped.splice(pinIdx, 1);
      mapped.unshift(pinned);
    }
  }

  return mapped;
}

// ---------------------------------------------------------------------------
// Pure fetch functions (no hooks — used by both useQuery and fetchQuery)
// ---------------------------------------------------------------------------

async function fetchUrlOptions(
  ds: UrlDataSource,
  search: string,
  extraParams?: Record<string, string | number | boolean>,
): Promise<SelectOption[]> {
  const effectiveParams = { ...(ds.params ?? {}), ...(extraParams ?? {}) };
  const queryParams: Record<string, string | number | boolean> = {};

  let resolvedUrl = ds.url;
  for (const [key, value] of Object.entries(effectiveParams)) {
    const placeholder = `{${key}}`;
    if (resolvedUrl.includes(placeholder)) {
      resolvedUrl = resolvedUrl.replace(placeholder, encodeURIComponent(String(value)));
    } else {
      queryParams[key] = value;
    }
  }

  if (/\{[^}]+\}/.test(resolvedUrl)) {
    return [];
  }

  if (ds.searchParam && search) {
    queryParams[ds.searchParam] = search;
  }
  const cleanUrl = resolvedUrl.replace(/^\//, '');
  const resp = await axios.get(cleanUrl, {
    params: Object.keys(queryParams).length > 0 ? queryParams : undefined,
  });
  return mapUrlItems(unwrapEnvelope<Record<string, unknown>>(resp.data), ds);
}

async function fetchFnOptions(
  ds: FnDataSource,
  search: string,
  resolvedParams?: Record<string, string | number | boolean>,
): Promise<SelectOption[]> {
  // Merge ds-level params (static, declared in FnDataSource) with runtime
  // resolvedParams (dynamic, from dataSourceParams / dependsOn watching).
  const effectiveParams = { ...(ds.params ?? {}), ...(resolvedParams ?? {}) };
  const result = await ds.fn(search, effectiveParams);
  const raw = unwrapEnvelope(result);
  return raw.map(ds.mapper);
}

// ---------------------------------------------------------------------------
// useAsyncOptions — internal hook for all three modes
// ---------------------------------------------------------------------------

interface UseAsyncOptionsReturn {
  loadOptions: (
    inputValue: string,
    callback: (options: SelectOption[]) => void,
  ) => void;
  allOptions: SelectOption[];
  isInitialLoading: boolean;
}

interface UseAsyncOptionsConfig {
  dataSource: DataSource | undefined;
  debounceMs: number;
  enabled?: boolean;
  resolvedParams?: Record<string, string | number | boolean>;
}

export function useAsyncOptions({
  dataSource,
  debounceMs,
  enabled = true,
  resolvedParams,
}: UseAsyncOptionsConfig): UseAsyncOptionsReturn {
  const queryClient = useQueryClient();
  // For fn-mode: merge static ds.params with runtime resolvedParams so cascading
  // selects (e.g. FETCH_STATES filtered by countryId) get a distinct cache entry.
  const mergedFnParams = React.useMemo(() => {
    if (!dataSource || dataSource.type !== 'fn') return undefined;
    const merged = { ...(dataSource.params ?? {}), ...(resolvedParams ?? {}) };
    return Object.keys(merged).length > 0 ? merged : undefined;
  }, [dataSource, resolvedParams]);

  const mergedFnParamsKey = React.useMemo(
    () => (mergedFnParams ? JSON.stringify(mergedFnParams, Object.keys(mergedFnParams).sort()) : ''),
    [mergedFnParams],
  );

  // fnKey includes runtime params so state(countryA) ≠ state(countryB) in cache.
  const fnKey = React.useMemo(() => {
    if (dataSource?.type !== 'fn') return '';
    const base = fnIdentity(dataSource);
    return mergedFnParamsKey ? `${base}:${mergedFnParamsKey}` : base;
  }, [dataSource, mergedFnParamsKey]);

  const [searchResults, setSearchResults] = React.useState<SelectOption[]>([]);
  const timerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const mergedParams = React.useMemo(() => {
    if (!dataSource || dataSource.type !== 'url') return undefined;
    return { ...(dataSource.params ?? {}), ...(resolvedParams ?? {}) };
  }, [dataSource, resolvedParams]);

  const paramsKey = React.useMemo(
    () => (mergedParams ? JSON.stringify(mergedParams) : ''),
    [mergedParams],
  );

  // Stable query key for initial (no-search) fetch
  const dsUrl = dataSource?.type === 'url' ? dataSource.url : '';
  const initialQueryKey = React.useMemo(
    (): unknown[] => {
      if (!dataSource) return ['async-select-disabled'];
      if (dataSource.type === 'url') return ['async-select', dsUrl, paramsKey];
      // fnKey already encodes cacheKey + merged params — distinct per param set
      if (dataSource.type === 'fn') return ['async-select-fn', fnKey];
      return ['async-select-disabled'];
    },
    [dataSource?.type, dsUrl, paramsKey, fnKey],
  );

  // Initial load via useQuery — replaces useEffect + useState + _optionsCache.
  // React Query handles caching, deduplication, cancellation, and refetch-on-key-change.
  const { data: initialOptions = [], isLoading: isInitialLoading } = useQuery<SelectOption[]>(
    initialQueryKey,
    async () => {
      if (!dataSource) return [];
      if (dataSource.type === 'url') {
        return fetchUrlOptions(dataSource, '', resolvedParams);
      }
      if (dataSource.type === 'fn') {
        // Pass merged params so the action (e.g. FetchStatesAction) can filter
        // by countryId, employeeId, etc. without the caller needing to know.
        return fetchFnOptions(dataSource, '', mergedFnParams);
      }
      return [];
    },
    {
      enabled: enabled && !!dataSource && dataSource.type !== 'custom',
      staleTime: ASYNC_SELECT_STALE_TIME,
      cacheTime: ASYNC_SELECT_CACHE_TIME,
      // Dropdown options are static lookup lists — window-focus refetching is wasteful
      // and was the primary cause of spurious API calls during normal form interaction
      // (e.g. clicking DevTools or returning to tab triggered a full options refetch).
      refetchOnWindowFocus: false,
    },
  );

  // Clear accumulated search results when params change (cascading selects)
  React.useEffect(() => {
    setSearchResults([]);
  }, [paramsKey, mergedFnParamsKey]);

  // Merge initial + search results for value resolution.
  // Search results accumulate so that a previously selected search result
  // can still resolve its label even after the user clears the search box.
  const allOptions = React.useMemo(() => {
    if (!enabled) return [];
    if (searchResults.length === 0) return initialOptions;
    const merged = [...initialOptions];
    for (const o of searchResults) {
      if (!merged.some((e) => e.value === o.value)) merged.push(o);
    }
    return merged;
  }, [enabled, initialOptions, searchResults]);

  // loadOptions for react-select — debounced callback that uses
  // queryClient.fetchQuery for deduplication and cache sharing.
  const loadOptionsCb = React.useCallback(
    (inputValue: string, callback: (options: SelectOption[]) => void) => {
      if (!enabled || !dataSource) {
        callback([]);
        return;
      }
      if (timerRef.current != null) clearTimeout(timerRef.current);

      timerRef.current = setTimeout(async () => {
        try {
          let options: SelectOption[];

          if (dataSource.type === 'url') {
            const ds = dataSource;
            if (ds.searchParam) {
              options = await queryClient.fetchQuery(
                ['async-select', ds.url, paramsKey, inputValue],
                () => fetchUrlOptions(ds, inputValue, resolvedParams),
                { staleTime: ASYNC_SELECT_STALE_TIME, cacheTime: ASYNC_SELECT_CACHE_TIME },
              );
            } else {
              const lower = inputValue.toLowerCase();
              callback(
                initialOptions.filter((o) =>
                  o.label.toLowerCase().includes(lower),
                ),
              );
              return;
            }
          } else if (dataSource.type === 'fn') {
            if (dataSource.searchMode === 'server') {
              options = await queryClient.fetchQuery(
                ['async-select-fn', fnKey, inputValue],
                () => fetchFnOptions(dataSource, inputValue),
                { staleTime: ASYNC_SELECT_STALE_TIME, cacheTime: ASYNC_SELECT_CACHE_TIME },
              );
            } else {
              const lower = inputValue.toLowerCase();
              callback(
                initialOptions.filter((o) =>
                  o.label.toLowerCase().includes(lower),
                ),
              );
              return;
            }
          } else {
            options = await dataSource.loadOptions(inputValue);
          }

          setSearchResults((prev) => {
            const merged = [...prev];
            for (const o of options) {
              if (!merged.some((e) => e.value === o.value)) merged.push(o);
            }
            return merged;
          });
          callback(options);
        } catch {
          callback([]);
        }
      }, debounceMs);
    },
    [dataSource, debounceMs, enabled, resolvedParams, paramsKey, initialOptions, queryClient, fnKey],
  );

  return { loadOptions: loadOptionsCb, allOptions, isInitialLoading };
}

// ---------------------------------------------------------------------------
// Custom indicators — match the DS Select component appearance
// ---------------------------------------------------------------------------

function WlDropdownIndicator(props: React.ComponentProps<typeof components.DropdownIndicator>) {
  if (props.hasValue) {
    return <></>;
  }
  return (
    <components.DropdownIndicator {...props}>
      <span className="material-symbols-rounded" aria-hidden>
        arrow_drop_down
      </span>
    </components.DropdownIndicator>
  );
}

function WlClearIndicator(props: React.ComponentProps<typeof components.ClearIndicator>) {
  return (
    <components.ClearIndicator {...props}>
      <span
        className="material-symbols-rounded"
        style={{ fontSize: '16px', fontWeight: 'bold' }}
        aria-hidden
      >
        close
      </span>
    </components.ClearIndicator>
  );
}

export const asyncSelectComponents = {
  DropdownIndicator: WlDropdownIndicator,
  ClearIndicator: WlClearIndicator,
};

function createReselectAwareAsyncSelectComponents(options: {
  reselectOptionLabel: string;
  onReselectSameOption: () => void;
}) {
  const targetLabel = options.reselectOptionLabel.toLowerCase();

  function ReselectAwareOption(
    props: React.ComponentProps<typeof components.Option>,
  ): React.ReactElement {
    const innerProps = {
      ...props.innerProps,
      onMouseDown: (event: React.MouseEvent<HTMLDivElement>) => {
        props.innerProps.onMouseDown?.(event);
        const optionLabel = String(props.label ?? '').toLowerCase();
        if (props.isSelected && optionLabel === targetLabel) {
          options.onReselectSameOption();
        }
      },
    };
    return <components.Option {...props} innerProps={innerProps} />;
  }

  return {
    DropdownIndicator: WlDropdownIndicator,
    ClearIndicator: WlClearIndicator,
    Option: ReselectAwareOption,
  };
}

// ---------------------------------------------------------------------------
// Component props
// ---------------------------------------------------------------------------

export interface RhfAsyncSelectProps extends RhfLayoutProps, RhfFormFieldShellProps {
  control: Control<FieldValues>;
  name: string;
  label: string;

  /**
   * Defines how options are fetched. Three modes:
   *
   * - `{ type: 'url', url, labelField, valueField }` — fetch from a URL
   * - `{ type: 'fn', fn, mapper }` — call an @api-services function
   * - `{ type: 'custom', loadOptions }` — raw callback (escape hatch)
   */
  dataSource?: DataSource;

  placeholder?: string;
  required?: boolean;
  semanticId?: string;
  disabled?: boolean;
  isMulti?: boolean;
  /**
   * Presentation for multi-select only.
   * - `'chips'` (default) — react-select async multi with chip values
   * - `'checkbox'` — closed select trigger + checkbox list (legacy ElectionCustomDropdown parity)
   */
  multiVariant?: 'chips' | 'checkbox';
  /**
   * When true, multi-select disables options from `displayOrder` + current value
   * (legacy Job Scheduler configuration). Does not refetch on each selection.
   */
  displayOrderDisableGate?: boolean;
  isClearable?: boolean;

  /**
   * When true, options are loaded on mount with an empty string.
   * When false, options load only after the user types.
   * @default true
   */
  defaultOptions?: boolean | SelectOption[];

  /** Debounce wait time in ms before calling loadOptions after user types. */
  debounceMs?: number;

  noOptionsMessage?: string;
  loadingMessage?: string;

  /**
   * Cache loaded options so the same inputValue doesn't trigger a re-fetch.
   * @default true
   */
  cacheOptions?: boolean;

  // ---------------------------------------------------------------------------
  // Cascading async select support (Phase E)
  // ---------------------------------------------------------------------------

  /**
   * Maps API query param names to sibling form field names.
   * The adapter watches these fields and passes their values as extra params
   * to the data source. When params change, options are refetched.
   *
   * ```tsx
   * <RhfAsyncSelect
   *   dataSourceParams={{ countryId: 'employmentCountryId' }}
   *   dataSource={{ type: 'url', url: '/api/states', ... }}
   * />
   * ```
   */
  dataSourceParams?: Record<string, string>;

  /**
   * When true, clears this field's value when dataSourceParams change
   * (after initial hydration). Prevents stale child selections when
   * a parent dropdown changes.
   * @default false
   */
  resetOnParamsChange?: boolean;

  /**
   * When false, `dataSourceParams` are merged into fetch params when present but do
   * not block the initial load (optional include-params for edit hydration).
   * When true or omitted, at least one watched param must be non-empty before fetch
   * (cascading child dropdown). Form engine sets this from `dependsOn`.
   * @default undefined — treated as true when `dataSourceParams` is set
   */
  cascadeRequiresParent?: boolean;

  /**
   * Controls whether options should be loaded. When false, the select
   * shows no options and does not fetch. Useful for gating on parent selection.
   * @default true
   */
  enabled?: boolean;

  /**
   * Required when `resetOnParamsChange` is true or when `effects` are used.
   * Used to clear/set field values.
   */
  setValue?: UseFormSetValue<FieldValues>;

  /** Inbound reactions — watches sibling fields and computes prop overrides for this field. */
  reactions?: Reaction[];

  /** Outbound effects — when this field's value changes, clear/set sibling fields. */
  effects?: Effect[];

  /**
   * Callback from the form engine to store option metadata when a selection changes.
   * Called with the selected value and the full option object for option-prop lookups.
   */
  onOptionChange?: (value: string, option: Record<string, unknown>) => void;

  /**
   * When the user selects a non-empty plan document, clears session-store chips
   * for the paired document type (via form-engine `onExclusiveStoreClear`).
   */
  onExclusiveStoreClear?: () => void;

  /** Option values to hide from the menu (client-side; does not change fetch). */
  excludeOptionValues?: string[];

  /**
   * When the user clicks an already-selected menu option whose label matches
   * `reselectOptionLabel` (react-select skips onChange). Job scheduler Custom recurrence.
   */
  reselectOptionLabel?: string;
  onReselectSameOption?: () => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Async select adapter for React Hook Form.
 *
 * Wraps `react-select/async` with the DS theme, FormField label/error/hint,
 * and RHF Controller binding.
 *
 * ## Mode 1: URL-based (mirrors table V2 dataSource)
 *
 * ```tsx
 * <RhfAsyncSelect
 *   control={control}
 *   name="countryId"
 *   label="Country"
 *   dataSource={{
 *     type: 'url',
 *     url: '/api/customer/api/app/country/countries',
 *     labelField: 'name',
 *     valueField: 'id',
 *   }}
 * />
 * ```
 *
 * ## Mode 2: Service function
 *
 * ```tsx
 * <RhfAsyncSelect
 *   control={control}
 *   name="participantId"
 *   label="Participant"
 *   dataSource={{
 *     type: 'fn',
 *     fn: (search) => ParticipantService.getParticipantsForTax(search),
 *     mapper: (item) => ({
 *       label: item.participantName ?? '',
 *       value: item.participantId ?? '',
 *       department: item.department,
 *     }),
 *   }}
 * />
 * ```
 *
 * ## Mode 3: Custom loadOptions (escape hatch)
 *
 * ```tsx
 * <RhfAsyncSelect
 *   control={control}
 *   name="someField"
 *   label="Custom"
 *   dataSource={{
 *     type: 'custom',
 *     loadOptions: async (search) => {
 *       const res = await complexFetch(search);
 *       return res.map(x => ({ label: x.display, value: x.code }));
 *     },
 *   }}
 * />
 * ```
 */
export const RhfAsyncSelect = React.forwardRef<HTMLDivElement, RhfAsyncSelectProps>(
  function RhfAsyncSelect(
    {
      control,
      name,
      label,
      dataSource,
      placeholder = 'Select',
      required = false,
      hint,
      tooltip,
      semanticId,
      disabled = false,
      isMulti = false,
      multiVariant = 'chips',
      displayOrderDisableGate = false,
      isClearable = true,
      defaultOptions = true,
      debounceMs = 300,
      noOptionsMessage,
      loadingMessage,
      cacheOptions = true,
      dataSourceParams,
      resetOnParamsChange = false,
      cascadeRequiresParent,
      enabled = true,
      setValue,
      reactions,
      effects,
      onOptionChange,
      onExclusiveStoreClear,
      excludeOptionValues,
      reselectOptionLabel,
      onReselectSameOption,
      controlWidth,
    },
    ref,
  ) {
    const { theme } = useTheme();

    const selectComponents = React.useMemo(() => {
      if (onReselectSameOption && reselectOptionLabel) {
        return createReselectAwareAsyncSelectComponents({
          reselectOptionLabel,
          onReselectSameOption,
        });
      }
      return asyncSelectComponents;
    }, [onReselectSameOption, reselectOptionLabel]);
    const overrides = useReactions(control, reactions);
    const { runEffects } = useEffects(effects, setValue);

    const notifyExclusiveStoreClear = (newVal: string | string[]): void => {
      if (!onExclusiveStoreClear) return;
      const scalar = Array.isArray(newVal) ? newVal[0] : newVal;
      if (scalar) onExclusiveStoreClear();
    };

    const finalDisabled = overrides.disabled ?? disabled;
    const finalRequired = (overrides.required as boolean) ?? required;
    const finalLabel = (overrides.label as string) ?? label;
    const finalEnabled = (overrides.enabled as boolean) ?? enabled;

    if (overrides.visible === false) return null;

    // Watch parent fields specified in dataSourceParams
    const watchedFieldNames = React.useMemo(
      () => (dataSourceParams ? Object.values(dataSourceParams) : []),
      [dataSourceParams],
    );
    const watchedValues = useWatch({ control, name: watchedFieldNames as string[] });

    // Build resolved params: { apiParamName: watchedFieldValue }
    const resolvedParams = React.useMemo(() => {
      if (!dataSourceParams) return undefined;
      const params: Record<string, string | number | boolean> = {};
      const paramEntries = Object.entries(dataSourceParams);
      paramEntries.forEach(([apiParam, _fieldName], index) => {
        const value = Array.isArray(watchedValues) ? watchedValues[index] : watchedValues;
        if (value == null || value === '') return;
        if (Array.isArray(value)) {
          params[apiParam] = JSON.stringify(value);
          return;
        }
        params[apiParam] = value as string | number | boolean;
      });
      return Object.keys(params).length > 0 ? params : undefined;
    }, [dataSourceParams, watchedValues]);

    const resolvedParamsKey = React.useMemo(
      () => (resolvedParams ? JSON.stringify(resolvedParams) : ''),
      [resolvedParams],
    );

    // Track whether initial hydration is complete to avoid clearing on first render
    const isHydratedRef = React.useRef(false);
    const prevParamsKeyRef = React.useRef(resolvedParamsKey);

    React.useEffect(() => {
      if (!isHydratedRef.current) {
        isHydratedRef.current = true;
        prevParamsKeyRef.current = resolvedParamsKey;
        return;
      }

      if (resetOnParamsChange && setValue && prevParamsKeyRef.current !== resolvedParamsKey) {
        setValue(name, isMulti ? [] : '');
      }
      prevParamsKeyRef.current = resolvedParamsKey;
    }, [resolvedParamsKey, resetOnParamsChange, setValue, name, isMulti]);

    // Cascading child: wait for parent. Optional paramMap-only fields still fetch on mount.
    const mustWaitForParent =
      cascadeRequiresParent !== false && Boolean(dataSourceParams);
    const cascadeReady = !mustWaitForParent || resolvedParams != null;

    const {
      loadOptions: debouncedLoader,
      allOptions: rawAllOptions,
      isInitialLoading,
    } = useAsyncOptions({ dataSource, debounceMs, enabled: finalEnabled && cascadeReady, resolvedParams });

    const excludeKey = excludeOptionValues?.map(String).join(',') ?? '';
    const allOptions = React.useMemo(
      () => filterExcludedSelectOptions(rawAllOptions, excludeOptionValues),
      [rawAllOptions, excludeKey, excludeOptionValues],
    );
    const loadOptions = React.useCallback(
      (input: string, callback: (options: SelectOption[]) => void) => {
        debouncedLoader(input, (opts) => {
          callback(filterExcludedSelectOptions(opts, excludeOptionValues));
        });
      },
      [debouncedLoader, excludeKey, excludeOptionValues],
    );

    // fn-mode (schema actionId dropdowns): disable react-select loadOptions cache so
    // invalidateAsyncSelectOptions + React Query refetch surfaces new options on menu open.
    // url/custom modes keep the default cacheOptions prop — zero change for legacy URL selects.
    const resolvedCacheOptions = dataSource?.type === 'fn' ? false : cacheOptions;

    const resolvedDefaultOptions = React.useMemo(() => {
      if (Array.isArray(defaultOptions)) return defaultOptions;
      if (defaultOptions === true) return allOptions;
      return false as const;
    }, [defaultOptions, allOptions]);

    const currentValue = useWatch({ control, name });
    const multiSelectOptions = React.useMemo(
      () => applyDisplayOrderDisableGate(allOptions, currentValue, displayOrderDisableGate && isMulti),
      [allOptions, currentValue, displayOrderDisableGate, isMulti],
    );
    const hydrationDoneRef = React.useRef(false);

    React.useEffect(() => {
      if (!onOptionChange || hydrationDoneRef.current) return;
      if (!currentValue || allOptions.length === 0) return;

      if (isMulti) {
        const vals: string[] = Array.isArray(currentValue) ? currentValue : [];
        if (vals.length === 0) return;
        const matched = allOptions.filter((o) => vals.includes(o.value));
        if (matched.length > 0) {
          matched.forEach((o) => onOptionChange(o.value, o as unknown as Record<string, unknown>));
          hydrationDoneRef.current = true;
        }
      } else {
        const strVal = String(currentValue);
        const matched = allOptions.find((o) => o.value === strVal);
        if (matched) {
          onOptionChange(strVal, matched as unknown as Record<string, unknown>);
          hydrationDoneRef.current = true;
        }
      }
    }, [currentValue, allOptions, onOptionChange, isMulti]);

    const autoDefaultAppliedRef = React.useRef(false);
    React.useEffect(() => {
      if (autoDefaultAppliedRef.current) return;
      if (!dataSource || dataSource.type !== 'url') return;
      if (!dataSource.autoDefault || !dataSource.pinFirst) return;
      if (allOptions.length === 0) return;
      if (currentValue) return;

      const { field, value } = dataSource.pinFirst;
      const defaultOption = allOptions.find((o) => (o as Record<string, unknown>)[field] === value);
      if (defaultOption && setValue) {
        setValue(name, defaultOption.value);
        if (onOptionChange) {
          onOptionChange(defaultOption.value, defaultOption as unknown as Record<string, unknown>);
        }
        autoDefaultAppliedRef.current = true;
      }
    }, [allOptions, currentValue, dataSource, setValue, name, onOptionChange]);

    return (
      <Controller
        control={control}
        name={name}
        render={({ field, fieldState, formState }) => {
          const errorText = rhfInlineError(fieldState, { isSubmitted: formState.isSubmitted });
          const baseStyles = createWealthlaneReactSelectStyles<SelectOption, boolean>(theme, {
            hasError: Boolean(errorText),
          });
          const semantic = getSemanticAttributes(semanticId);

          // Normalize both sides to string for comparison so numeric/boolean option values
          // (e.g. companyTypes uses numeric enum 1/2/3) correctly match stored form values
          // regardless of their runtime type. Storage is always the raw primitive from the
          // data action (string UUID or number enum) — we never coerce in onChange.
          const findOption = (val: unknown): SelectOption | null =>
            allOptions.find((o) => String(o.value) === String(val)) ?? null;

          const findOptions = (vals: unknown[]): SelectOption[] =>
            allOptions.filter((o) => vals.map(String).includes(String(o.value)));

          if (isMulti) {
            const multiValue: string[] = Array.isArray(field.value)
              ? field.value.map(String)
              : [];
            const optionList = displayOrderDisableGate ? multiSelectOptions : allOptions;
            const findOptionsInList = (vals: string[]): SelectOption[] =>
              optionList.filter((o) => vals.includes(String(o.value)));
            const selectedOptions = findOptionsInList(multiValue);
            const isMultiOptionDisabled = (option: SelectOption) => {
              if (displayOrderDisableGate) {
                const gated = multiSelectOptions;
                return Boolean(
                  gated.find((o) => String(o.value) === String(option.value))?.isDisabled,
                );
              }
              return Boolean(option.isDisabled);
            };
            const commitMultiValues = (rawValues: string[]) => {
              const values = displayOrderDisableGate
                ? normalizeDisplayOrderMultiSelection(rawValues, optionList)
                : rawValues;
              field.onChange(values);
              runEffects(values);
              if (onOptionChange) {
                findOptionsInList(values).forEach((o) => {
                  onOptionChange(o.value, o as unknown as Record<string, unknown>);
                });
              }
            };

            if (multiVariant === 'checkbox') {
              return (
                <FormField
                  ref={ref}
                  label={finalLabel}
                  hint={hint}
                  tooltip={tooltip}
                  required={finalRequired}
                  semanticId={semanticId}
                  disabled={finalDisabled}
                  error={errorText}
                  controlWidth={controlWidth}
                >
                  <div {...semantic} style={{ width: '100%', maxWidth: '100%' }}>
                    <CheckboxSelect
                      name={name}
                      semanticId={semanticId ?? name}
                      options={optionList}
                      value={multiValue}
                      onChange={(values) => commitMultiValues(values)}
                      onBlur={field.onBlur}
                      placeholder={placeholder}
                      disabled={finalDisabled}
                      loading={isInitialLoading}
                      error={Boolean(errorText)}
                      emptyMessage={noOptionsMessage ?? 'No options'}
                    />
                  </div>
                </FormField>
              );
            }

            return (
              <FormField
                ref={ref}
                label={finalLabel}
                hint={hint}
                tooltip={tooltip}
                required={finalRequired}
                semanticId={semanticId}
                disabled={finalDisabled}
                error={errorText}
                controlWidth={controlWidth}
              >
                <div {...semantic} style={{ width: '100%', maxWidth: '100%' }}>
                  <RSAsyncSelect<SelectOption, true, GroupBase<SelectOption>>
                    name={name}
                    inputId={semanticId ?? name}
                    loadOptions={loadOptions}
                    defaultOptions={resolvedDefaultOptions}
                    cacheOptions={resolvedCacheOptions}
                    value={selectedOptions}
                    onChange={(opts) => {
                      const selected = opts as MultiValue<SelectOption>;
                      commitMultiValues(selected.map((o) => o.value));
                    }}
                    onBlur={field.onBlur}
                    placeholder={placeholder}
                    isDisabled={finalDisabled}
                    isLoading={isInitialLoading}
                    isMulti
                    isClearable={isClearable}
                    isOptionDisabled={isMultiOptionDisabled}
                    styles={baseStyles}
                    components={asyncSelectComponents}
                    classNamePrefix="wl-select"
                    menuPosition="fixed"
                    menuPlacement="auto"
                    menuShouldBlockScroll
                    menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                    minMenuHeight={200}
                    maxMenuHeight={300}
                    backspaceRemovesValue={false}
                    aria-invalid={Boolean(errorText)}
                    noOptionsMessage={() => noOptionsMessage ?? 'No options'}
                    loadingMessage={() => loadingMessage ?? 'Loading…'}
                  />
                </div>
              </FormField>
            );
          }

          const selectedOption = field.value != null && field.value !== '' ? findOption(field.value) : null;

          return (
            <FormField
              ref={ref}
              label={finalLabel}
              hint={hint}
              tooltip={tooltip}
              required={finalRequired}
              semanticId={semanticId}
              disabled={finalDisabled}
              error={errorText}
              controlWidth={controlWidth}
            >
              <div {...semantic} style={{ width: '100%', maxWidth: '100%' }}>
                <RSAsyncSelect<SelectOption, false, GroupBase<SelectOption>>
                  name={name}
                  inputId={semanticId ?? name}
                  loadOptions={loadOptions}
                  defaultOptions={resolvedDefaultOptions}
                  cacheOptions={resolvedCacheOptions}
                  value={selectedOption}
                  onChange={(opt) => {
                    const o = opt as SingleValue<SelectOption>;
                    const newVal = o?.value ?? '';
                    field.onChange(newVal);
                    runEffects(newVal, o as SelectOption & Record<string, unknown>);
                    notifyExclusiveStoreClear(newVal);
                    if (onOptionChange && o) {
                      onOptionChange(newVal, o as unknown as Record<string, unknown>);
                    }
                  }}
                  onBlur={field.onBlur}
                  placeholder={placeholder}
                  isDisabled={finalDisabled}
                  isLoading={isInitialLoading}
                  isClearable={isClearable}
                  styles={baseStyles}
                  components={selectComponents}
                  classNamePrefix="wl-select"
                  menuPosition="fixed"
                  menuPlacement="auto"
                  menuShouldBlockScroll
                  menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                  minMenuHeight={200}
                  maxMenuHeight={300}
                  aria-invalid={Boolean(errorText)}
                  noOptionsMessage={() => noOptionsMessage ?? 'No options'}
                  loadingMessage={() => loadingMessage ?? 'Loading…'}
                />
              </div>
            </FormField>
          );
        }}
      />
    );
  },
);

RhfAsyncSelect.displayName = 'RhfAsyncSelect';
